import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Api as ApiIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  externalSystemService,
  type ExternalSystemDto,
  type ExternalSystemFilters,
} from '../services/externalSystemService';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * External systems list page component.
 */
export const ExternalSystemsListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [systems, setSystems] = useState<ExternalSystemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<ExternalSystemFilters>({
    pageNumber: 1,
    pageSize: 20,
    searchTerm: '',
    isActive: undefined,
  });

  const loadSystems = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await externalSystemService.getExternalSystems(filters);
      setSystems(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load external systems');
      addNotification('Failed to load external systems', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSystems();
  }, [filters.pageNumber, filters.pageSize, filters.isActive, filters.searchTerm]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this external system? This will also delete all associated API methods.')) {
      return;
    }

    try {
      await externalSystemService.deleteExternalSystem(id);
      addNotification('External system deleted successfully', 'success');
      loadSystems();
    } catch (err: any) {
      addNotification(err.message || 'Failed to delete external system', 'error');
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<ExternalSystemDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (system) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {system.name}
          </Typography>
          {system.description && (
            <Typography variant="caption" color="text.secondary">
              {system.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'baseUrl',
      label: 'Base URL',
      minWidth: 250,
      render: (system) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {system.baseUrl}
        </Typography>
      ),
    },
    {
      id: 'authenticationType',
      label: 'Authentication',
      minWidth: 150,
      render: (system) => (
        <Chip label={system.authenticationType} size="small" variant="outlined" />
      ),
    },
    {
      id: 'apiMethodCount',
      label: 'API Methods',
      minWidth: 120,
      render: (system) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApiIcon fontSize="small" color="action" />
          <Typography variant="body2">{system.apiMethodCount}</Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (system) => (
        <Chip
          label={system.isActive ? 'Active' : 'Inactive'}
          color={system.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      render: (system) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => navigate(`/external-systems/${system.id}`)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/external-systems/${system.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(system.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 20));

  return (
    <PageListLayout
      title="External Systems"
      createButton={{ label: 'Create External System', onClick: () => navigate('/external-systems/new'), icon: <AddIcon /> }}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      loading={loading}
      isEmpty={systems.length === 0}
      emptyState={{
        title: 'No external systems found',
        description: 'Create a new external system to get started.',
        action: { label: 'Create External System', onClick: () => navigate('/external-systems/new') },
      }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search external systems"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filters.isActive === true}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    isActive: e.target.checked ? true : undefined,
                    pageNumber: 1,
                  })
                }
              />
            }
            label="Active Only"
          />
        </Box>
      }
      pagination={{
        page: filters.pageNumber || 1,
        totalPages,
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={systems}
      />
    </PageListLayout>
  );
};
